import { Building2, FolderKanban, Home, Palette, Plus, Settings, UserRound, Users } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { homePath } from "./currentOrg";
import { useMe } from "@/api/hooks/auth";
import { useOrgs } from "@/api/hooks/orgs";
import { useProjects } from "@/api/hooks/projects";
import { STATUS_CLASSES } from "@/components/StatusPill";
import { cn } from "@/lib/utils";

const linkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "relative flex min-h-11 items-center gap-3 rounded-md px-3 py-2 font-heading text-base font-medium no-underline transition-colors duration-fast",
    isActive
      ? "bg-secondary-hover text-fg-inverse before:absolute before:inset-y-2 before:left-0 before:w-1 before:rounded-pill before:bg-brand before:content-empty"
      : "text-fg-inverse-muted hover:bg-secondary-hover hover:text-fg-inverse",
  );

function Item({
  to,
  icon,
  children,
  end,
  onNavigate,
}: {
  to: string;
  icon: ReactNode;
  children: ReactNode;
  end?: boolean;
  onNavigate?: () => void;
}) {
  return (
    <li>
      <NavLink to={to} end={end} className={linkClass} onClick={onNavigate}>
        <span aria-hidden="true" className="flex h-5 w-5 shrink-0 items-center justify-center">
          {icon}
        </span>
        <span className="min-w-0 truncate">{children}</span>
      </NavLink>
    </li>
  );
}

function Group({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <p className="px-3 pt-4 text-xs font-semibold uppercase tracking-wide text-fg-inverse-muted">{label}</p>
      <ul className="flex flex-col gap-1">{children}</ul>
    </div>
  );
}

function OrgProjects({ org, canManage, onNavigate }: { org: string; canManage: boolean; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const projects = useProjects(org);
  const orgs = useOrgs();
  const orgName = orgs.data?.find((o) => o.slug === org)?.name ?? org;
  return (
    <Group label={orgName}>
      {(projects.data ?? []).map((p) => (
        <Item
          key={p.id}
          to={`/${org}/${p.slug}`}
          onNavigate={onNavigate}
          icon={<span className={cn("h-2.5 w-2.5 rounded-full", STATUS_CLASSES[p.status])} />}
        >
          {p.name}
        </Item>
      ))}
      {canManage ? (
        <>
          <Item to={`/${org}/projects/new`} icon={<Plus className="h-5 w-5" />} onNavigate={onNavigate}>
            {t("shell.newProject")}
          </Item>
          <Item to={`/${org}/members`} icon={<Users className="h-5 w-5" />} onNavigate={onNavigate}>
            {t("shell.members")}
          </Item>
        </>
      ) : null}
    </Group>
  );
}

/** Main navigation: used in the desktop sidebar and in the mobile sheet. */
export function SidebarNav({ org, onNavigate }: { org: string | null; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const me = useMe();
  const isStaff = Boolean(me.data?.is_staff);
  const role = isStaff ? "staff" : me.data?.memberships.find((m) => m.organisation_slug === org)?.role;
  return (
    <nav aria-label={t("common.nav.main")} className="flex flex-col gap-1">
      <ul className="flex flex-col gap-1">
        <Item to={homePath(me.data)} end icon={<Home className="h-5 w-5" />} onNavigate={onNavigate}>
          {t("common.nav.home")}
        </Item>
      </ul>
      {org ? <OrgProjects org={org} canManage={role === "owner" || role === "staff"} onNavigate={onNavigate} /> : null}
      {isStaff ? (
        <Group label={t("common.nav.admin")}>
          <Item to="/admin" end icon={<Building2 className="h-5 w-5" />} onNavigate={onNavigate}>
            {t("admin.nav.orgs")}
          </Item>
          <Item to="/admin/projects" icon={<FolderKanban className="h-5 w-5" />} onNavigate={onNavigate}>
            {t("admin.nav.projects")}
          </Item>
          <Item to="/admin/users" icon={<Users className="h-5 w-5" />} onNavigate={onNavigate}>
            {t("admin.nav.users")}
          </Item>
          <Item to="/admin/settings" icon={<Settings className="h-5 w-5" />} onNavigate={onNavigate}>
            {t("admin.nav.settings")}
          </Item>
          <Item to="/design-system" icon={<Palette className="h-5 w-5" />} onNavigate={onNavigate}>
            {t("admin.nav.designSystem")}
          </Item>
        </Group>
      ) : null}
      <ul className="mt-4 flex flex-col gap-1 border-t border-secondary-hover pt-4">
        <Item to="/account" icon={<UserRound className="h-5 w-5" />} onNavigate={onNavigate}>
          {t("common.nav.account")}
        </Item>
      </ul>
    </nav>
  );
}
